import React, { useEffect, useState } from "react";

import { faClock } from "@fortawesome/free-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Page, Divider, Stepper } from "@samskip/frontend-components";
import { format } from "date-fns";
import { PageProps, navigate } from "gatsby";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

import { CommonConstants, NavigationConstants } from "@assets/constants";
import { BookingTemplateFormTypes } from "@assets/formTypes";
import { useAppDispatch, useAppSelector, useLeavePrompt } from "@assets/hooks";
import { BookingTemplateMappings } from "@assets/mappings";
import { BookingTemplateTypes, RegistryTypes } from "@assets/types";
import { translate } from "@assets/utilities/translateUtilities";
import {
  LoadingModal,
  SpinnerPage,
  BookingTemplatesCargoForm,
  BookingTemplatesRouteForm,
} from "@components";
import NotFound from "@pages/notFound";
import BookingTemplateService from "@services/BookingTemplateService";
import { getPorts } from "@state/slices/portsSlice";
import { getAllCountries, getIncoterms } from "@state/slices/registrySlice";
import { userProfileState } from "@state/slices/userProfileSlice";

interface BookingTemplatesCreateProps extends PageProps {
  mode: NavigationConstants.PAGE_MODE;
  bookingTemplateId: number;
}

const BookingTemplatesCreate = ({
  mode,
  bookingTemplateId,
}: BookingTemplatesCreateProps) => {
  const { t } = useTranslation();
  const steps = [{ title: t("LABEL_ROUTE") }, { title: t("LABEL_CARGO") }];
  const [currentStep, setCurrentStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [dirty, setDirty] = useState(true);
  const [areDefaultsLoaded, setAreDefaultsLoaded] = useState(false);
  const [formValues, setFormValues] =
    useState<BookingTemplateFormTypes.BookingTemplateForm>({
      ...BookingTemplateFormTypes.defaultRouteForm,
      ...BookingTemplateFormTypes.defaultCargoForm,
    });
  const [currentBookingTemplate, setCurrentBookingTemplate] =
    useState<BookingTemplateTypes.BookingTemplate | null>(null);

  const { userProfile, isSwitchingCompanies } =
    useAppSelector(userProfileState);

  const isPageLoading = !areDefaultsLoaded || isSwitchingCompanies;

  useLeavePrompt(dirty);
  const dispatch = useAppDispatch();

  const [bookingTemplateNotFound, setBookingTemplateNotFound] = useState(false);

  const getTitleText = () => {
    switch (mode) {
      case NavigationConstants.PAGE_MODE.UPDATE:
        return t("LABEL_EDIT_BOOKING_TEMPLATE");
      case NavigationConstants.PAGE_MODE.COPY:
        return t("LABEL_COPY_BOOKING_TEMPLATE");
      default:
        return t("LABEL_CREATE_BOOKING_TEMPLATE");
    }
  };

  const updateFormValues = (
    values:
      | BookingTemplateFormTypes.RouteForm
      | BookingTemplateFormTypes.CargoForm
  ) => {
    setFormValues((prevValues) => ({
      ...prevValues,
      ...values,
    }));
  };

  useEffect(() => {
    const initializeFormWithBookingTemplate = async (
      incoterms: RegistryTypes.Incoterm[]
    ) => {
      try {
        const bookingTemplate = await BookingTemplateService.getTemplate(
          bookingTemplateId
        );
        setCurrentBookingTemplate(bookingTemplate);
        const bookingTemplateForm =
          await BookingTemplateMappings.bookingTemplateToTemplateForm(
            bookingTemplate,
            incoterms
          );
        if (mode === NavigationConstants.PAGE_MODE.COPY) {
          bookingTemplateForm.BookingTemplateName = `${t("LABEL_COPY_OF_N", {
            n: bookingTemplateForm.BookingTemplateName,
          })}`;
        }
        updateFormValues(bookingTemplateForm);
      } catch {
        setBookingTemplateNotFound(true);
      }
    };

    Promise.all([
      dispatch(getIncoterms()).unwrap(),
      dispatch(getPorts()).unwrap(),
      dispatch(getAllCountries()).unwrap(),
    ])
      .then((values) => {
        if (
          mode === NavigationConstants.PAGE_MODE.UPDATE ||
          mode === NavigationConstants.PAGE_MODE.COPY
        ) {
          initializeFormWithBookingTemplate(values[0]).then(() =>
            setAreDefaultsLoaded(true)
          );
        } else {
          setAreDefaultsLoaded(true);
        }
      })
      .catch(() => {
        toast.error(t("LABEL_ERROR_OCCURRED"));
        setAreDefaultsLoaded(true);
      });
  }, []);

  useEffect(() => {
    if (mode === NavigationConstants.PAGE_MODE.CREATE) {
      updateFormValues({
        ...BookingTemplateFormTypes.defaultRouteForm,
        ...BookingTemplateFormTypes.defaultCargoForm,
      });
    } else {
      if (
        currentBookingTemplate !== null &&
        currentBookingTemplate.BookingParty !==
          userProfile?.SelectedCompany.PartnerCode
      ) {
        navigate(`${NavigationConstants.NAVIGATION_LINKS.bookingTemplates}`);
      }
    }
  }, [userProfile?.SelectedCompany.PartnerCode]);

  const handlePrevClick = () => {
    if (currentStep > 0) {
      setCurrentStep((page) => page - 1);
    }
  };
  const handleNextClick = () => {
    if (steps && currentStep < steps.length) {
      setCurrentStep((page) => page + 1);
    }
  };

  const handleNextFormSubmit = (
    values:
      | BookingTemplateFormTypes.RouteForm
      | BookingTemplateFormTypes.CargoForm
  ) => {
    if (currentStep === steps.length - 1) {
      // take the before values, and the current one, to deal with the delay (root, values of the Cargo)
      handleSubmitForm({ ...formValues, ...values });
    } else {
      updateFormValues(values);
      handleNextClick();
    }
  };

  const handlePrevFormSubmit = (values: BookingTemplateFormTypes.CargoForm) => {
    updateFormValues(values);
    handlePrevClick();
  };

  // ------------ SUBMITTING -----------------

  const createBookingTemplate = (
    bookingTemplateData: BookingTemplateTypes.CreateUpdateBookingTemplate
  ) => {
    BookingTemplateService.createTemplate(bookingTemplateData)
      .then(() => {
        toast.success(translate("LABEL_TEMPLATE_CREATED_SUCCESS"));
        navigate(`${NavigationConstants.NAVIGATION_LINKS.bookingTemplates}`);
      })
      .catch(() => {
        toast.error(translate("ERROR_TEMPLATE_CREATE_FAILED"));
      })
      .finally(() => setIsSubmitting(false));
  };

  const editBookingTemplate = (
    bookingTemplateData: BookingTemplateTypes.CreateUpdateBookingTemplate
  ) => {
    BookingTemplateService.updateTemplate(
      bookingTemplateId,
      bookingTemplateData
    )
      .then(() => {
        toast.success(translate("LABEL_TEMPLATE_EDITED_SUCCESS"));
        navigate(`${NavigationConstants.NAVIGATION_LINKS.bookingTemplates}`);
      })
      .catch(() => {
        toast.error(translate("ERROR_TEMPLATE_EDIT_FAILED"));
      })
      .finally(() => setIsSubmitting(false));
  };

  const handleSubmitForm = (
    formData: BookingTemplateFormTypes.BookingTemplateForm
  ) => {
    setIsSubmitting(true);
    const requestObj =
      BookingTemplateMappings.templateFormToCreateUpdateBookingTemplate(
        formData
      );
    switch (mode) {
      case NavigationConstants.PAGE_MODE.UPDATE:
        return editBookingTemplate(requestObj);
      default:
        return createBookingTemplate(requestObj);
    }
  };

  const renderDateForUpdate = (
    date: Date | null,
    userName: string,
    isLastUsedLabel = false
  ) => {
    const formattedDate =
      date && format(new Date(date), CommonConstants.DATE_FORMAT);
    const formattedUser = userName;
    return (
      <div className="flex flex-row gap-2 items-baseline text-neutral-800">
        {date && (
          <>
            <FontAwesomeIcon
              className="text-neutral-600 mt-0.5"
              icon={faClock}
            />
            {isLastUsedLabel ? (
              <p>
                {t("LABEL_USED_DATE_AND_USER", {
                  LastUsedDate: formattedDate,
                  LastUsedBy: formattedUser,
                })}
              </p>
            ) : (
              <p>
                {t("LABEL_MODIFIED_DATE_AND_USER", {
                  ModifiedDate: formattedDate,
                  ModifiedBy: formattedUser,
                })}
              </p>
            )}
          </>
        )}
        {!date && isLastUsedLabel && (
          <>
            <FontAwesomeIcon
              className="text-neutral-600 mt-0.5"
              icon={faClock}
            />
            <p>{t("LABEL_NOT_PREVIOUSLY_USED")}</p>
          </>
        )}
      </div>
    );
  };

  if (bookingTemplateNotFound) {
    return <NotFound />;
  }

  if (isPageLoading) {
    return <SpinnerPage />;
  }

  return (
    <Page
      actions={
        mode === NavigationConstants.PAGE_MODE.UPDATE &&
        currentBookingTemplate && (
          <div className="flex flex-col gap-1">
            {renderDateForUpdate(
              currentBookingTemplate.LastUsedDate,
              currentBookingTemplate.LastUsedBy,
              true
            )}
            {renderDateForUpdate(
              currentBookingTemplate.ModifiedDate,
              currentBookingTemplate.ModifiedBy
            )}
          </div>
        )
      }
      size="medium"
      title={getTitleText()}
    >
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
        <Stepper activeStep={currentStep} steps={steps} variant="Samskip" />
      </div>
      <Divider space="large" />
      {currentStep === 0 && !isPageLoading && (
        <BookingTemplatesRouteForm
          formValues={formValues}
          onCancelClick={() => setDirty(false)}
          onNextClick={handleNextFormSubmit}
          pageMode={mode}
          updateTemplateId={currentBookingTemplate?.Id}
        />
      )}
      {currentStep === 1 && !isPageLoading && (
        <BookingTemplatesCargoForm
          formValues={formValues}
          isSubmitting={isSubmitting}
          onCancelClick={() => setDirty(false)}
          onNextClick={handleNextFormSubmit}
          onPrevClick={handlePrevFormSubmit}
        />
      )}
      <LoadingModal isOpen={isSubmitting}>
        {t("TEXT_LOADING_TEMPLATE_REQUEST")}
      </LoadingModal>
    </Page>
  );
};

export default BookingTemplatesCreate;
